/**
 * Phase 2 MM9 — Prospects (internal target/client records).
 * Real API-backed list + search/filter + create + edit/archive panel.
 */

import { useCallback, useEffect, useState } from "react";
import {
  archiveProspect, createProspect, getProspects, patchProspect,
} from "../api/client";
import { Button } from "../components/Button";
import { StatusPill } from "../components/StatusPill";
import { studioStrings } from "../i18n/studioStrings";
import { useLocale } from "../i18n/useLocale";
import { AppLayout } from "./CatalogLayout";

type Row = Record<string, unknown>;
const TYPES = ["restaurant", "cloud_kitchen", "cafe", "food_brand",
  "beverage_brand", "beauty_brand", "supplement_brand", "retail_brand", "other"];
const STATUSES = ["target", "contacted", "interested", "proposal_sent", "won", "lost"];

export function ProspectsPage(): JSX.Element {
  const [locale] = useLocale();
  const t = studioStrings(locale);
  const [items, setItems] = useState<Row[]>([]);
  const [state, setState] = useState<"loading" | "ok" | "denied" | "error">("loading");
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ name: "", prospect_type: "restaurant",
    country: "", city: "", notes: "" });
  const [selected, setSelected] = useState<Row | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const r = await getProspects(search ? { search } : undefined);
    if (r.outcome === "ok") { setItems(r.data?.items ?? []); setState("ok"); }
    else setState(r.status === 403 ? "denied" : "error");
  }, [search]);

  useEffect(() => { void refresh(); }, [refresh]);

  async function onCreate(): Promise<void> {
    if (form.name.trim().length < 2) { setNotice(t.loadError); return; }
    const r = await createProspect({ ...form, notes: form.notes || undefined,
      country: form.country || undefined, city: form.city || undefined });
    setNotice(r.outcome === "ok" ? null
      : r.status === 403 ? t.permissionDenied : r.errorMessage);
    if (r.outcome === "ok") setForm({ name: "", prospect_type: "restaurant",
      country: "", city: "", notes: "" });
    await refresh();
  }

  async function onStatus(id: string, status: string): Promise<void> {
    const r = await patchProspect(id, { status });
    setNotice(r.outcome === "ok" ? null : r.errorMessage);
    await refresh();
  }

  return (
    <AppLayout>
      <h1>{t.prospectsTitle}</h1>
      {notice ? <p role="alert" className="po-error">{notice}</p> : null}
      {state === "denied" ? <p role="alert">{t.permissionDenied}</p> : null}
      {state === "error" ? <p role="alert">{t.loadError}</p> : null}
      {state === "loading" ? <p role="status">…</p> : null}

      <section className="po-row" aria-label={t.newProspect} data-testid="prospect-create">
        <input placeholder={t.nameLabel} value={form.name}
               onChange={(e) => setForm({ ...form, name: e.target.value })}
               data-testid="prospect-name" />
        <select value={form.prospect_type} data-testid="prospect-type"
                onChange={(e) => setForm({ ...form, prospect_type: e.target.value })}>
          {TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <input placeholder={t.countryLabel} value={form.country}
               onChange={(e) => setForm({ ...form, country: e.target.value })} />
        <input placeholder={t.cityLabel} value={form.city}
               onChange={(e) => setForm({ ...form, city: e.target.value })} />
        <Button variant="primary" onClick={() => void onCreate()}>{t.newProspect}</Button>
      </section>

      <section className="po-row">
        <input placeholder={t.searchLabel} value={search}
               onChange={(e) => setSearch(e.target.value)} data-testid="prospect-search" />
      </section>

      {state === "ok" && items.length === 0 ? (
        <p data-testid="prospects-empty">{t.emptyList}</p>
      ) : (
        <table className="po-table" data-testid="prospects-table">
          <thead><tr><th>{t.nameLabel}</th><th>{t.typeLabel}</th>
            <th>{t.cityLabel}</th><th>{t.statusLabel}</th><th /></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={String(p.id)} onClick={() => setSelected(p)}>
                <td>{String(p.name)}</td>
                <td>{String(p.prospect_type)}</td>
                <td>{String(p.city ?? "—")}</td>
                <td><StatusPill tone={p.status === "won" ? "ok" : "info"}
                                label={String(p.status)} /></td>
                <td>
                  <select value={String(p.status)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => void onStatus(String(p.id), e.target.value)}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>{" "}
                  <Button variant="secondary"
                          onClick={() => void archiveProspect(String(p.id)).then(refresh)}>
                    {t.archive}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selected ? (
        <section data-testid="prospect-detail">
          <h2>{String(selected.name)}</h2>
          <textarea defaultValue={String(selected.notes ?? "")}
                    placeholder={t.notesLabel}
                    onBlur={(e) => void patchProspect(String(selected.id),
                      { notes: e.target.value }).then(refresh)} />
        </section>
      ) : null}
    </AppLayout>
  );
}
